import { get, merge, set } from 'lodash';
import { useAction, useDataPath } from './context';
import { useCustomViewNunjucksRenderer } from './context';
import { useMemo } from 'react';
import { NUNJUCKS_PREPROCESS_PROPS } from './constants';
import { WbSafeNunjucksApi } from '../../apis';

type CustomViewProcessorContext = {
  nunjucksRenderer: WbSafeNunjucksApi;
};

type CustomViewProcessor = (
  data: any,
  props: Record<string, any>,
  context: CustomViewProcessorContext,
) => Record<string, any>;

type CustomViewHideEvaluator = (
  data: any,
  props: Record<string, any>,
  context: CustomViewProcessorContext,
) => boolean;

function not(func: CustomViewHideEvaluator): CustomViewHideEvaluator {
  return (data, props, context) => !func(data, props, context);
}

export function processComplexCondition(
  data: any,
  condition: Record<string, any>,
  context: CustomViewProcessorContext,
): boolean {
  const parsedValue = context.nunjucksRenderer.render(condition.value, data);
  if (parsedValue) {
    const parsedEqual = condition.equals?.toString();
    const parsedNotEqual = condition.notEquals?.toString();
    return (
      (!parsedEqual && !parsedNotEqual) ||
      (parsedEqual && parsedValue === parsedEqual) ||
      (parsedNotEqual && parsedValue !== parsedNotEqual)
    );
  }
  return false;
}

const useProcessMode: CustomViewProcessor = (_, props) => {
  const mode = useAction('getMode')() || '';
  return { ...props, mode };
};

const processDynamicProps: CustomViewProcessor = (data, props, context) => {
  // when this is true, we skip the nunjucks processing for this component props and forward them as is
  if (props.__disableNunjucksProcessing) return props;
  const dynamicProps = NUNJUCKS_PREPROCESS_PROPS.reduce(
    (acc: any, path: string) => {
      const prop = get(props, path);
      if (prop) {
        const parsedProperty = context.nunjucksRenderer.render(prop, data);
        // since the "merge" function doesn't replace undefined values, we need to set them to null
        // when not defined, to avoid having the old value in the final object
        return set(acc, path, parsedProperty ?? null);
      }
      return acc;
    },
    {},
  );
  return merge({}, props, dynamicProps);
};

// If path is defined, it will be resolved and act as value (overriding the actual value prop if is also defined)
const processPathValue: CustomViewProcessor = (data, props) => {
  const { path } = props;
  if (path !== undefined) {
    const value = path === '' ? data : get(data, path);
    return { ...props, value };
  }
  return props;
};

const processDefaultValue: CustomViewProcessor = (_, props) => {
  const { default: defaultValue } = props;
  if (defaultValue && !props.value) {
    return { ...props, value: defaultValue };
  }
  return props;
};

const processors: CustomViewProcessor[] = [
  useProcessMode,
  processDynamicProps,
  processPathValue,
  processDefaultValue,
];

const evaluateShowWhenExists: CustomViewHideEvaluator = (
  data,
  props,
): boolean => {
  const { showWhenExists } = props;
  if (showWhenExists) {
    const showWhenExistsList =
      typeof showWhenExists === 'string' ? [showWhenExists] : showWhenExists;
    return showWhenExistsList.some((showWhenExistsPath: string) => {
      const obj = get(data, showWhenExistsPath);
      if (obj) {
        if (typeof obj === 'object') {
          return !Object.values(obj).every(
            val => val === undefined || val === null,
          );
        }
        return true;
      }
      return false;
    });
  }
  return true;
};

const evaluateHideWhenExists: CustomViewHideEvaluator = (
  data,
  props,
): boolean => {
  const { hideWhenExists } = props;
  return hideWhenExists && get(data, hideWhenExists) !== undefined;
};

const evaluateShowWhen: CustomViewHideEvaluator = (
  data,
  props,
  context,
): boolean => {
  const { showWhen } = props;
  if (showWhen?.value) {
    return processComplexCondition(data, showWhen, context);
  }
  return true;
};

const evaluateShowWhenHasValue: CustomViewHideEvaluator = (
  _,
  props,
): boolean => {
  return !props.showWhenHasValue || props.value;
};

const hideFunctions: CustomViewHideEvaluator[] = [
  not(evaluateShowWhenExists),
  evaluateHideWhenExists,
  not(evaluateShowWhen),
  not(evaluateShowWhenHasValue),
];

export const useCustomViewPreProcessor = (
  originalProps: Record<string, any>,
) => {
  const inputProps = { ...originalProps };
  const data = useDataPath('');
  const nunjucksRenderer = useCustomViewNunjucksRenderer();

  const context = useMemo<CustomViewProcessorContext>(() => {
    return { nunjucksRenderer };
  }, [nunjucksRenderer]);

  const props = processors.reduce((properties, fn) => {
    return fn(data, properties, context);
  }, inputProps);

  const hidden = hideFunctions.some(fn => fn(data, props, context));
  return { props, hidden };
};
